import { MEETING_STATUSES, SSE_EVENTS } from "@lexicanon/shared"
import { useEffect, useRef, useState } from "react"
import { useAuth } from "../../hooks/useAuth"
import { createReconnectingEventSource } from "../../hooks/useSSE"

/**
 * Live reprocess progress, fed by the company-wide SSE stream filtered to the
 * given meeting. Three event families matter:
 *
 *   - `pipeline_started`  — emitted by the graph executor with the working
 *     set of instances about to run. Lets the modal render every expected
 *     step in `pending` state up front, before any actually starts. Without
 *     this, rows pop in as each step starts and the user has no preview of
 *     "what's still ahead."
 *   - `pipeline_step`     — emitted before AND after each step's execute.
 *     Carries `{ alias, ref, name, status, durationMs?, errorMessage? }`.
 *     Status transitions: pending → running → (completed | failed | skipped).
 *     This is the deterministic, every-step stream we render the live
 *     stepper from.
 *   - `processing_stage`  — kept as a secondary signal for backward compat
 *     and for steps that emit semantic stage info (e.g. final-analysis's
 *     `compacted` / `truncated` / `analyzing` sub-phases).
 *
 * The terminal "pipeline finished" signal is the meeting-tracking event
 * with `phase === "summarized"` — that's the moment the analysis has
 * landed in the DB and the page can refetch.
 */
export type TStepStatus = "pending" | "running" | "completed" | "failed" | "skipped"

export interface IStep {
  alias: string
  ref: string
  /** Display label from the step impl's `name` field. */
  name: string
  status: TStepStatus
  /** Wall-clock ms reported by the executor on terminal events. */
  durationMs?: number
  /** Error / skip reason when status ∈ { failed, skipped }. */
  errorMessage?: string
  /** Receipt time of the most recent transition — drives "x seconds ago" UI. */
  updatedAt: number
}

export interface IReprocessProgress {
  /**
   * Ordered list of all steps the executor announced via `pipeline_started`.
   * Each entry's `status` is updated in place as `pipeline_step` events
   * arrive. Empty until the first `pipeline_started` event arrives — the
   * modal shows a "preparing" placeholder until then.
   */
  steps: IStep[]
  /** Count of `progressive_summary` chunks observed (LLM chunked summaries). */
  progressiveSummaryChunks: number
  /** Most recent `processing_stage` event for legacy semantic sub-phases. */
  legacyStage: { stage: string; receivedAt: number } | null
  /** True once the meeting's tracked phase flips to `summarized`. */
  done: boolean
  /** True if any pipeline_step came in with status === "failed". */
  errored: boolean
  /** Latest error message (when `errored`). */
  errorMessage: string | null
  /** EventSource open state — surfaces network blips. */
  connected: boolean
}

const INITIAL: IReprocessProgress = {
  steps: [],
  progressiveSummaryChunks: 0,
  legacyStage: null,
  done: false,
  errored: false,
  errorMessage: null,
  connected: false,
}

interface IOptions {
  /** When false, the hook tears down its EventSource and resets state. */
  enabled: boolean
  /** Meeting whose events we care about. */
  meetingId: string
}

interface IPipelineStartedPayload {
  type?: unknown
  meetingId?: unknown
  runId?: unknown
  fromAlias?: unknown
  instances?: Array<{ alias?: unknown; ref?: unknown; name?: unknown }>
}

interface IPipelineStepPayload {
  type?: unknown
  meetingId?: unknown
  runId?: unknown
  alias?: unknown
  ref?: unknown
  name?: unknown
  status?: unknown
  durationMs?: unknown
  errorMessage?: unknown
}

function isStatus(value: unknown): value is TStepStatus {
  return (
    value === "pending" || value === "running" || value === "completed" || value === "failed" || value === "skipped"
  )
}

export function useReprocessProgress({ enabled, meetingId }: IOptions): IReprocessProgress {
  const [progress, setProgress] = useState<IReprocessProgress>(INITIAL)
  const { user } = useAuth()
  const companyId = user?.activeOrganizationId
  const meetingIdRef = useRef(meetingId)
  meetingIdRef.current = meetingId

  useEffect(() => {
    if (!enabled || !companyId || !meetingId) {
      setProgress(INITIAL)
      return
    }

    setProgress(INITIAL)

    // Latch onto the FIRST runId we see (from `pipeline_started`). A reprocess
    // spawns one run; if a stale/overlapping run's events arrive on the shared
    // per-company stream, ignoring any other runId keeps this modal's progress
    // from interleaving two runs' steps. Reset per effect run (new meeting).
    let latchedRunId: string | null = null

    const handleStatus = (connected: boolean): void => {
      setProgress((p) => ({ ...p, connected }))
    }

    const handleMessage = (event: MessageEvent<string>): void => {
      let data: Record<string, unknown>
      try {
        const parsed: unknown = JSON.parse(event.data)
        if (typeof parsed !== "object" || parsed === null) return
        data = parsed as Record<string, unknown>
      } catch {
        return
      }

      // Per-company stream — drop everything not addressed to our meeting.
      if (data.meetingId !== meetingIdRef.current) return

      const type = typeof data.type === "string" ? data.type : ""

      if (type === SSE_EVENTS.PIPELINE_STARTED) {
        const payload = data as IPipelineStartedPayload
        const runId = typeof payload.runId === "string" ? payload.runId : null
        // Latch the first run; ignore a second `pipeline_started` from any other
        // run (e.g. a racing recovery pass) so its steps don't overwrite ours.
        if (latchedRunId === null) {
          latchedRunId = runId
        } else if (runId !== null && runId !== latchedRunId) {
          return
        }
        const instances = Array.isArray(payload.instances) ? payload.instances : []
        const now = Date.now()
        const steps: IStep[] = instances.flatMap((i) => {
          const alias = typeof i.alias === "string" ? i.alias : ""
          const ref = typeof i.ref === "string" ? i.ref : alias
          const name = typeof i.name === "string" && i.name ? i.name : alias
          if (!alias) return []
          return [{ alias, ref, name, status: "pending", updatedAt: now }]
        })
        setProgress((p) => ({ ...p, steps }))
        return
      }

      if (type === SSE_EVENTS.PIPELINE_STEP) {
        const payload = data as IPipelineStepPayload
        // Drop step events from any run other than the latched one. Once latched,
        // a null runId (older backend) is accepted for back-compat.
        const runId = typeof payload.runId === "string" ? payload.runId : null
        if (latchedRunId !== null && runId !== null && runId !== latchedRunId) return
        const alias = typeof payload.alias === "string" ? payload.alias : ""
        if (!alias) return
        const status = isStatus(payload.status) ? payload.status : "running"
        const ref = typeof payload.ref === "string" ? payload.ref : alias
        const name = typeof payload.name === "string" && payload.name ? payload.name : alias
        const durationMs = typeof payload.durationMs === "number" ? payload.durationMs : undefined
        const errorMessage = typeof payload.errorMessage === "string" ? payload.errorMessage : undefined
        const now = Date.now()

        setProgress((p) => {
          const idx = p.steps.findIndex((s) => s.alias === alias)
          const updated: IStep = {
            alias,
            ref,
            name,
            status,
            durationMs,
            errorMessage,
            updatedAt: now,
          }
          // If we got a `pipeline_step` before the `pipeline_started`
          // announcement (e.g. SSE backpressure reordering), append the
          // step on the fly so the UI still surfaces it.
          const steps = idx === -1 ? [...p.steps, updated] : p.steps.map((s, i) => (i === idx ? updated : s))
          const next: IReprocessProgress = { ...p, steps }
          if (status === "failed") {
            next.errored = true
            next.errorMessage = errorMessage ?? "Pipeline step failed"
          }
          return next
        })
        return
      }

      if (type === SSE_EVENTS.PROCESSING_STAGE) {
        const stage = typeof data.stage === "string" ? data.stage : ""
        if (!stage) return
        setProgress((p) => ({ ...p, legacyStage: { stage, receivedAt: Date.now() } }))
        return
      }

      if (type === SSE_EVENTS.PROGRESSIVE_SUMMARY) {
        setProgress((p) => ({ ...p, progressiveSummaryChunks: p.progressiveSummaryChunks + 1 }))
        return
      }

      // Tracked-meeting heartbeat: the pipeline writes phase=summarized once
      // the final analysis has been persisted (via setMeetingAnalysis). For
      // Voxcanon templates the image / report fan-out runs *after* that, so
      // this is the earliest moment the user can usefully refetch.
      const phase = typeof data.phase === "string" ? data.phase : null
      if (phase === MEETING_STATUSES.SUMMARIZED) {
        setProgress((p) => ({ ...p, done: true }))
      } else if (phase === MEETING_STATUSES.ERROR) {
        // The reprocess backstop flips the meeting to `error` when the run
        // throws (including failures before any pipeline_step is emitted), so
        // mirror that into the modal's failure state instead of looking stuck.
        setProgress((p) => ({ ...p, errored: true, errorMessage: p.errorMessage ?? "Reprocess failed" }))
      }
    }

    // Route through the shared reconnecting wrapper so a transient 5xx / pod
    // restart re-subscribes with backoff (and flips `connected` back on) instead
    // of leaving the progress modal silently stalled.
    return createReconnectingEventSource(`/api/companies/${companyId}/events`, false, handleMessage, handleStatus)
  }, [enabled, companyId, meetingId])

  return progress
}
